import Text from '@creator/ui/components/Text/Text';
import { isExternal } from '@creator/ui/utils/url-utils';
import type { ParagraphData } from '@editorjs/paragraph';
import { cleanHtml } from '@src/utils/html-utils/html-utils';
import type { FC } from 'react';
import { memo } from 'react';

export interface EditorViewOnlyParagraphToolProps {
    className?: string;
    blockData: ParagraphData;
}

const EditorViewOnlyParagraphTool: FC<EditorViewOnlyParagraphToolProps> = props => {
    const { blockData, className } = props;

    function processExternalLinks(html: string): string {
        // Parse HTML and add security attributes to external links
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const links = tempDiv.querySelectorAll('a[href]');
        links.forEach(link => {
            const href = link.getAttribute('href');
            if (href && isExternal(href)) {
                link.setAttribute('target', '_blank');
                link.setAttribute('rel', 'noopener noreferrer');
            }
        });

        return tempDiv.innerHTML;
    }

    function renderContent() {
        const { text } = blockData;

        // Replace embed patterns with iframe HTML
        const _html = cleanHtml(text);
        const htmlWithEmbed = _html.replace(/\[embed\](.*?)\[\/embed\]/g, (_match, url) => {
            return `<iframe 
                title="Embedded Content" 
                class="aspect-video"
                src="${ensureEmbedUrl(url)}" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                allowfullscreen>
            </iframe>`;
        });

        // Process external links to add security attributes
        const processedHtml = processExternalLinks(htmlWithEmbed);

        return (
            <div className="ce-paragraph cdx-block">
                <p dangerouslySetInnerHTML={{ __html: processedHtml }} />
            </div>
        );
    }

    function ensureEmbedUrl(url: string) {
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
        if (youtubeRegex.test(url)) {
            // Convert watch urls to embed urls
            const watchUrlRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=)(.+)$/;
            if (watchUrlRegex.test(url)) return `https://www.youtube.com/embed/${watchUrlRegex.exec(url)?.[4]}`;
            return url;
        }

        return url;
    }

    return <div className={`paragraph-tool ${className}`}>{renderContent()}</div>;
};

export default memo(EditorViewOnlyParagraphTool);
